import React, { useState, useEffect, useLayoutEffect } from 'react';
import { X, AlertCircle, Trophy, Bug, ChevronUp, ChevronDown, Lightbulb, Share2, Clock, Shuffle } from 'lucide-react';
import './Game.css';
import { diseases } from './diseases';
import PracticePaywall from './PracticePaywall';
import { signIn, startCheckout, confirmCheckout, isSubscriptionActive, openBillingPortal } from './subscription';
import {
  getTodayString,
  getDailyDisease,
  getMsUntilNextPuzzle,
  formatCountdown,
  EMPTY_STATS,
  getActiveStreak,
  recordWin
} from './daily';
import { getComparisonClass, getVectorComparisonClass } from './compare';

const STATE_KEY = 'diseaseGameTodayState';
const STATS_KEY = 'plagdleStats';
// Signed-in player for practice mode: { token, email } from the API
const PRACTICE_ACCOUNT_KEY = 'plagdlePracticeAccount';

const trackEvent = (name, params = {}) => {
  try {
    if (window.gtag) window.gtag('event', name, params);
  } catch (error) {
    // Silently fail - don't impact user experience
  }
};

// localStorage can throw (blocked site data, private mode, sandboxed iframes)
// and saved values can be corrupted. Never let that break the game.
const storageGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const storageSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // Game still works for this session, it just won't be saved
  }
};

const storageGetJSON = (key) => {
  try {
    const value = JSON.parse(storageGet(key));
    return value && typeof value === 'object' ? value : null;
  } catch (error) {
    return null;
  }
};

// Function to save the game state for a given puzzle date
const saveGameState = (date, diseaseName, guesses, gameWon, showHint, showSecondHint) => {
  storageSet(STATE_KEY, JSON.stringify({
    date,
    diseaseName,
    guesses,
    gameWon,
    showHint,
    showSecondHint
  }));

  // Only mark as "played" if the game is actually complete
  if (gameWon) {
    storageSet('diseaseGameLastPlayed', date);
  }
};

// Function to load the saved state for a given puzzle, if it belongs to it
const loadGameState = (date, diseaseName) => {
  const gameState = storageGetJSON(STATE_KEY);
  if (!gameState || gameState.date !== date || !Array.isArray(gameState.guesses)) return null;

  if (gameState.diseaseName) {
    return gameState.diseaseName === diseaseName ? gameState : null;
  }

  // Saves from before diseaseName was recorded: keep only if every guess was
  // judged against this same disease (the daily schedule may have changed).
  // With no guesses there is nothing to verify the puzzle against, so drop it.
  if (gameState.guesses.length === 0) return null;
  const matchesPuzzle = gameState.guesses.every(g => Boolean(g.correct) === (g.name === diseaseName));
  return matchesPuzzle ? gameState : null;
};

const loadStats = () => {
  const saved = storageGetJSON(STATS_KEY);
  if (saved) return { ...EMPTY_STATS, ...saved };

  // Migrate the legacy streak counter
  const legacyStreak = parseInt(storageGet('diseaseGameStreak'), 10) || 0;
  return {
    played: legacyStreak,
    currentStreak: legacyStreak,
    lastWonDate: storageGet('diseaseGameLastPlayed')
  };
};

const saveStats = (stats) => {
  storageSet(STATS_KEY, JSON.stringify(stats));
  storageSet('diseaseGameStreak', stats.currentStreak.toString());
};

export default function Game() {
  const [targetDisease, setTargetDisease] = useState(null);
  const [puzzleDate, setPuzzleDate] = useState(null);
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [gameWon, setGameWon] = useState(false);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [hasCompletedToday, setHasCompletedToday] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showSecondHint, setShowSecondHint] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [msUntilNext, setMsUntilNext] = useState(getMsUntilNextPuzzle());
  // Practice mode: unlimited random diseases, never saved and never counted in stats
  const [practice, setPractice] = useState(false);
  const [practiceAccount, setPracticeAccount] = useState(() => {
    const saved = storageGetJSON(PRACTICE_ACCOUNT_KEY);
    return saved && typeof saved.token === 'string' ? saved : null;
  });
  const [practiceActive, setPracticeActive] = useState(null); // null until checked with the server
  const [paywallBusy, setPaywallBusy] = useState(false);
  const [paywallError, setPaywallError] = useState('');

  const streak = getActiveStreak(stats, getTodayString());
  const practiceUnlocked = Boolean(practiceAccount) && practiceActive === true;
  const showPaywall = practice && !practiceUnlocked;

  useEffect(() => {
    initializeGame();
    setStats(loadStats());

    // Returning from Stripe Checkout
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (sessionId && practiceAccount) {
      window.history.replaceState(null, '', window.location.pathname);
      setPractice(true);
      confirmCheckout(practiceAccount.token, sessionId)
        .then(() => {
          setPracticeActive(true);
          trackEvent('practice_subscribed');
        })
        .catch(() => {
          setPracticeActive(false);
          setPaywallError("We couldn't confirm your payment. If you were charged, please contact us.");
        });
    } else if (practiceAccount) {
      checkSubscription(practiceAccount);
    }
    // Runs once on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deal a practice disease once practice mode is open and the subscription
  // is confirmed, in whichever order those happen. A layout effect, so the
  // daily board never flashes up in practice mode first.
  useLayoutEffect(() => {
    if (practice && practiceActive === true) startPractice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practice, practiceActive]);

  // Countdown to the next puzzle, shown once today's game is over.
  // Switch to the new puzzle when the UTC date changes while the win screen
  // is showing. A win that happens after midnight (on the previous puzzle)
  // keeps its win screen until the following midnight so it can be shared.
  useEffect(() => {
    if (!gameWon || practice) return undefined;
    const shownOn = getTodayString();
    const timer = setInterval(() => {
      setMsUntilNext(getMsUntilNextPuzzle());
      if (getTodayString() !== shownOn) initializeGame();
    }, 1000);
    return () => clearInterval(timer);
  }, [gameWon, puzzleDate, practice]);

  const initializeGame = () => {
    const today = getTodayString();
    const dailyDisease = getDailyDisease(today);
    setTargetDisease(dailyDisease);
    setPuzzleDate(today);
    setMsUntilNext(getMsUntilNextPuzzle());

    // Load today's game state if it exists
    const savedState = loadGameState(today, dailyDisease.name);
    if (savedState) {
      setGuesses(savedState.guesses || []);
      setGameWon(savedState.gameWon || false);
      setShowHint(savedState.showHint || false);
      setShowSecondHint(savedState.showSecondHint || false);
      setHasCompletedToday(Boolean(savedState.gameWon));
    } else {
      // No saved state for today, start fresh
      setGuesses([]);
      setGameWon(false);
      setHasCompletedToday(false);
      setShowHint(false);
      setShowSecondHint(false);
    }
  };

  const startPractice = () => {
    setPractice(true);
    setTargetDisease(current => {
      const others = diseases.filter(d => d.name !== current?.name);
      return others[Math.floor(Math.random() * others.length)];
    });
    setGuesses([]);
    setGameWon(false);
    setHasCompletedToday(false);
    setShowHint(false);
    setShowSecondHint(false);
    setCurrentGuess('');
    setSuggestions([]);
  };

  const signOutPractice = () => {
    storageSet(PRACTICE_ACCOUNT_KEY, 'null');
    setPracticeAccount(null);
    setPracticeActive(null);
    setPaywallError('');
  };

  // Returns whether the player can play practice games
  const checkSubscription = async (account) => {
    setPracticeActive(null);
    try {
      const active = await isSubscriptionActive(account.token);
      setPracticeActive(active);
      return active;
    } catch (error) {
      if (error.status === 401) {
        signOutPractice();
      } else {
        setPracticeActive(false);
        setPaywallError('Could not check your subscription. Please try again later.');
      }
      return false;
    }
  };

  const handleSignIn = async (credential) => {
    setPaywallError('');
    try {
      const account = await signIn(credential);
      storageSet(PRACTICE_ACCOUNT_KEY, JSON.stringify(account));
      setPracticeAccount(account);
      trackEvent('practice_sign_in');
      await checkSubscription(account);
    } catch (error) {
      setPaywallError('Google sign-in failed. Please try again.');
    }
  };

  const openPractice = () => {
    if (practice) return;
    // The effect above deals the first disease when unlocked
    setPractice(true);
    if (!practiceUnlocked) trackEvent('practice_paywall_view');
  };

  const openDaily = () => {
    if (!practice) return;
    setPractice(false);
    setCurrentGuess('');
    setSuggestions([]);
    initializeGame();
  };

  const handleSubscribe = async () => {
    setPaywallBusy(true);
    setPaywallError('');
    trackEvent('practice_checkout_start');
    try {
      await startCheckout(practiceAccount.token);
    } catch (error) {
      setPaywallBusy(false);
      setPaywallError('Could not open the checkout. Please try again.');
    }
  };

  const handleManageSubscription = async () => {
    try {
      await openBillingPortal(practiceAccount.token);
    } catch (error) {
      setPaywallError('Could not open subscription settings. Please try again.');
    }
  };

  const updateStats = (newStats) => {
    setStats(newStats);
    saveStats(newStats);
  };

  const handleInputChange = (e) => {
    // Don't allow input if already completed today
    if (hasCompletedToday) return;

    const value = e.target.value;
    setCurrentGuess(value);

    if (value.length > 0) {
      const filtered = diseases.filter(d =>
        d.name.toLowerCase().includes(value.toLowerCase()) &&
        !guesses.some(g => g.name === d.name)
      );
      setSuggestions(filtered.slice(0, 5));
    } else {
      setSuggestions([]);
    }
  };

  const makeGuess = (diseaseName) => {
    // Don't allow guesses if already completed today
    if (hasCompletedToday) return;

    const disease = diseases.find(d => d.name === diseaseName);
    if (!disease || guesses.some(g => g.name === disease.name)) return;

    const correct = disease.name === targetDisease.name;
    const newGuess = { ...disease, correct };

    const newGuesses = [...guesses, newGuess];
    setGuesses(newGuesses);
    setCurrentGuess('');
    setSuggestions([]);

    if (practice) {
      if (correct) {
        setGameWon(true);
        trackEvent('practice_complete', { guesses: newGuesses.length, disease_name: disease.name });
      }
      return;
    }

    if (newGuesses.length === 1) trackEvent('game_start', { disease_name: targetDisease.name });
    trackEvent('guess', { guess_number: newGuesses.length, correct });

    if (correct) {
      setGameWon(true);
      setHasCompletedToday(true);
      const newStats = recordWin(stats, puzzleDate);
      updateStats(newStats);
      saveGameState(puzzleDate, targetDisease.name, newGuesses, true, showHint, showSecondHint);

      trackEvent('game_complete', {
        guesses: newGuesses.length,
        disease_name: disease.name,
        used_hint_1: showHint,
        used_hint_2: showSecondHint,
        streak: newStats.currentStreak
      });
    } else {
      // Save progress but don't mark as completed
      saveGameState(puzzleDate, targetDisease.name, newGuesses, false, showHint, showSecondHint);
    }
  };

  const handleShowHint = () => {
    setShowHint(true);
    if (practice) return;
    saveGameState(puzzleDate, targetDisease.name, guesses, gameWon, true, showSecondHint);
    trackEvent('hint_used', { hint: 1, guesses: guesses.length });
  };

  const handleShowSecondHint = () => {
    setShowSecondHint(true);
    if (practice) return;
    saveGameState(puzzleDate, targetDisease.name, guesses, gameWon, showHint, true);
    trackEvent('hint_used', { hint: 2, guesses: guesses.length });
  };

  // Let multi-route vectors wrap after "/" instead of mid-word on narrow screens
  const renderVector = (vector) =>
    vector.split('/').map((route, idx) => (
      <React.Fragment key={idx}>
        {idx > 0 && <>/<wbr /></>}
        {route}
      </React.Fragment>
    ));

  const toSquare = (comparisonClass) =>
    comparisonClass === 'disease-game-exact-match' ? '🟩' :
    comparisonClass === 'disease-game-partial-match' ? '🟨' : '🟥';

  const buildShareText = () => {
    const squares = guesses.map(guess => [
      toSquare(getComparisonClass(guess.vaccine, targetDisease.vaccine, false)),
      toSquare(getComparisonClass(guess.etiology, targetDisease.etiology, false)),
      toSquare(getIncubationComparisonClass(guess.incubationMin, targetDisease.incubationMin).class),
      toSquare(getIncubationComparisonClass(guess.incubationMax, targetDisease.incubationMax).class),
      toSquare(getComparisonClass(guess.treatment, targetDisease.treatment, false)),
      toSquare(getVectorComparisonClass(guess.vector, targetDisease.vector))
    ].join('')).join('\n');

    const hintsUsed = [showHint, showSecondHint].filter(Boolean).length;
    const result = `Solved in ${guesses.length} ${guesses.length === 1 ? 'guess' : 'guesses'}`;
    const hintText = hintsUsed ? ` (💡${hintsUsed} ${hintsUsed === 1 ? 'hint' : 'hints'})` : '';
    const streakText = streak > 1 ? `\n🔥 ${streak}-day streak` : '';

    return `Plagdle ${puzzleDate} 🦠\n${result}${hintText}${streakText}\n\n${squares}\n\nhttps://plagdle.com`;
  };

  const handleShare = async () => {
    if (!gameWon || !targetDisease) return;
    const shareText = buildShareText();
    const isTouchDevice = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

    if (navigator.share && isTouchDevice) {
      try {
        await navigator.share({ text: shareText });
        trackEvent('share', { method: 'native' });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        // Fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
      setShareMessage('Copied to clipboard!');
      trackEvent('share', { method: 'clipboard' });
    } catch (err) {
      console.error('Failed to copy:', err);
      setShareMessage('Failed to copy');
    }
    setTimeout(() => setShareMessage(''), 2000);
  };

  // Function to get comparison class and direction for incubation periods
  const getIncubationComparisonClass = (guessValue, targetValue) => {
    if (guessValue === targetValue) {
      return { class: 'disease-game-exact-match', direction: null };
    }
    if (guessValue < targetValue) {
      return { class: 'disease-game-no-match', direction: 'up' };
    }
    return { class: 'disease-game-no-match', direction: 'down' };
  };

  return (
    <div className="disease-game-container">
      <div className="disease-game-wrapper">
        {/* Header */}
        <div className="disease-game-header">
          <div className="disease-game-title-wrapper">
            <Bug className="disease-game-bug-icon" />
            <h1 className="disease-game-title">Plagdle</h1>
            <Bug className="disease-game-bug-icon disease-game-bug-icon-flipped" />
          </div>
          <p className="disease-game-subtitle">Guess the infectious disease based on its characteristics!</p>

          {/* Mode switch */}
          <div className="disease-game-mode-switch">
            <button
              onClick={openDaily}
              className={`disease-game-mode-button ${!practice ? 'disease-game-mode-button-active' : ''}`}
            >
              Daily
            </button>
            <button
              onClick={openPractice}
              className={`disease-game-mode-button ${practice ? 'disease-game-mode-button-active' : ''}`}
            >
              Practice
              <span className="disease-game-new-badge">NEW</span>
            </button>
          </div>

          {/* Stats */}
          {!showPaywall && (
          <div className="disease-game-stats-container">
            <div className="disease-game-stat-box">
              <div className="disease-game-stat-number">{streak}</div>
              <div className="disease-game-stat-label">Streak</div>
            </div>
            <button
              onClick={handleShowHint}
              disabled={guesses.length < 3 || showHint}
              className={`disease-game-hint-button ${guesses.length < 3
                  ? 'disease-game-hint-button-disabled'
                  : showHint
                    ? 'disease-game-hint-button-active'
                    : ''
                }`}
            >
              <Lightbulb className="disease-game-hint-icon" />
              First Hint
            </button>
            <button
              onClick={handleShowSecondHint}
              disabled={guesses.length < 6 || showSecondHint}
              className={`disease-game-hint-button ${guesses.length < 6
                  ? 'disease-game-hint-button-disabled'
                  : showSecondHint
                    ? 'disease-game-hint-button-active'
                    : ''
                }`}
            >
              <Lightbulb className="disease-game-hint-icon" />
              Second Hint
            </button>
          </div>
          )}
        </div>

        {/* Game Area */}
        <div className="disease-game-area">
          {showPaywall ? (
            <PracticePaywall
              email={practiceAccount?.email}
              checking={practiceActive === null}
              busy={paywallBusy}
              error={paywallError}
              onSignIn={handleSignIn}
              onSignInError={() => setPaywallError('Could not load Google sign-in. Please try again later.')}
              onSignOut={signOutPractice}
              onSubscribe={handleSubscribe}
            />
          ) : (
          <>
          {/* Input */}
          {!gameWon && !hasCompletedToday && (
            <div className="disease-game-input-wrapper">
              <input
                type="text"
                value={currentGuess}
                onChange={handleInputChange}
                onKeyPress={(e) => e.key === 'Enter' && currentGuess && makeGuess(currentGuess)}
                placeholder="Type a disease name..."
                className="disease-game-input"
              />

              {/* Suggestions dropdown */}
              {suggestions.length > 0 && (
                <div className="disease-game-suggestions" style={{ zIndex: 1000 }}>
                  {suggestions.map((disease, idx) => (
                    <div
                      key={idx}
                      onClick={() => makeGuess(disease.name)}
                      className="disease-game-suggestion-item"
                    >
                      {disease.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}




          {/* Hint Display */}
          {(showHint || showSecondHint) && targetDisease && (
            <div className="disease-game-hint-display">
              {showHint && (
                <div className="disease-game-hint-content">
                  <div className="disease-game-hint-icon-wrapper">
                    <Lightbulb className="disease-game-hint-icon" />
                    <span className="disease-game-hint-number">1</span>
                  </div>
                  <span className="disease-game-hint-text">{targetDisease.smallHint}</span>
                </div>
              )}
              {showSecondHint && (
                <div className="disease-game-hint-content disease-game-second-hint">
                  <div className="disease-game-hint-icon-wrapper">
                    <Lightbulb className="disease-game-hint-icon" />
                    <span className="disease-game-hint-number">2</span>
                  </div>
                  <span className="disease-game-hint-text">{targetDisease.bigHint}</span>
                </div>
              )}
            </div>
          )}

          {/* Guesses Table */}
          {guesses.length > 0 && (
            <div className="disease-game-table-wrapper">
              <table className="disease-game-table">
                <thead>
                  <tr>
                    <th>Disease</th>
                    <th className="center">Vaccine</th>
                    <th className="center">Etiology</th>
                    <th className="center">
                      <span className="disease-game-th-full">Min Incubation</span>
                      <span className="disease-game-th-short">Min (days)</span>
                    </th>
                    <th className="center">
                      <span className="disease-game-th-full">Max Incubation</span>
                      <span className="disease-game-th-short">Max (days)</span>
                    </th>
                    <th className="center">Treatment</th>
                    <th className="center">Vector</th>
                  </tr>
                </thead>
                <tbody>
                  {guesses.map((guess, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="disease-game-disease-cell">
                          {guess.correct ? (
                            <Trophy className="disease-game-trophy-icon disease-game-button-icon" />
                          ) : (
                            <X className="disease-game-x-icon" />
                          )}
                          <span className="disease-game-disease-name">{guess.name}</span>
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getComparisonClass(guess.vaccine, targetDisease.vaccine, false)}`}>
                          {guess.vaccine}
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getComparisonClass(guess.etiology, targetDisease.etiology, false)}`}>
                          {guess.etiology}
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getIncubationComparisonClass(guess.incubationMin, targetDisease.incubationMin).class}`}>
                          <span className="disease-game-incubation-value">{guess.incubationMin}</span>
                          {getIncubationComparisonClass(guess.incubationMin, targetDisease.incubationMin).direction && (
                            <div className="disease-game-incubation-arrow">
                              {getIncubationComparisonClass(guess.incubationMin, targetDisease.incubationMin).direction === 'up' ? (
                                <ChevronUp className="disease-game-arrow-icon" />
                              ) : (
                                <ChevronDown className="disease-game-arrow-icon" />
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getIncubationComparisonClass(guess.incubationMax, targetDisease.incubationMax).class}`}>
                          <span className="disease-game-incubation-value">{guess.incubationMax}</span>
                          {getIncubationComparisonClass(guess.incubationMax, targetDisease.incubationMax).direction && (
                            <div className="disease-game-incubation-arrow">
                              {getIncubationComparisonClass(guess.incubationMax, targetDisease.incubationMax).direction === 'up' ? (
                                <ChevronUp className="disease-game-arrow-icon" />
                              ) : (
                                <ChevronDown className="disease-game-arrow-icon" />
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getComparisonClass(guess.treatment, targetDisease.treatment, false)}`}>
                          {guess.treatment}
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getVectorComparisonClass(guess.vector, targetDisease.vector)}`}>
                          {renderVector(guess.vector)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="disease-game-legend">
            <div className="disease-game-legend-item">
              <div className="disease-game-legend-box disease-game-exact-match"></div>
              <span>Exact Match</span>
            </div>
            <div className="disease-game-legend-item">
              <div className="disease-game-legend-box disease-game-partial-match"></div>
              <span>Partial Match</span>
            </div>
            <div className="disease-game-legend-item">
              <div className="disease-game-legend-box disease-game-no-match"></div>
              <span>No Match</span>
            </div>
          </div>

          {/* Win Message */}
          {gameWon && targetDisease && (
            <div className="disease-game-win-message">
              <Trophy className="disease-game-icon disease-game-trophy-icon" />
              <h2 className="disease-game-win-title">Congratulations!</h2>
              <p className="disease-game-win-text">You found the disease in {guesses.length} {guesses.length === 1 ? 'guess' : 'guesses'}!</p>

              {practice ? (
                <button onClick={startPractice} className="disease-game-share-button">
                  <Shuffle className="disease-game-share-icon" />
                  Next Disease
                </button>
              ) : (
              <>
              {/* Personal stats */}
              <div className="disease-game-summary-stats">
                <div className="disease-game-summary-stat">
                  <div className="disease-game-summary-number">{stats.played}</div>
                  <div className="disease-game-summary-label">Played</div>
                </div>
                <div className="disease-game-summary-stat">
                  <div className="disease-game-summary-number">{streak}</div>
                  <div className="disease-game-summary-label">Streak</div>
                </div>
              </div>

              {/* Share Button */}
              <button onClick={handleShare} className="disease-game-share-button">
                <Share2 className="disease-game-share-icon" />
                Share Results
              </button>
              {shareMessage && (
                <p className="disease-game-share-message">{shareMessage}</p>
              )}
              </>
              )}

              {/* Trivia Section */}
              <div className="disease-game-trivia-section">
                <div className="disease-game-trivia-header">
                  <Lightbulb className="disease-game-trivia-icon" />
                  <h3 className="disease-game-trivia-title">Fun Fact!</h3>
                </div>
                <p className="disease-game-trivia-text">{targetDisease.trivia}</p>
              </div>

              {!practice && (
              <>
              <div className="disease-game-countdown">
                <Clock className="disease-game-countdown-icon" />
                <span>Next disease in</span>
                <span className="disease-game-countdown-time">{formatCountdown(msUntilNext)}</span>
              </div>
              <p className="disease-game-win-subtext">Come back tomorrow to keep your streak going!</p>
              </>
              )}
            </div>
          )}

          {/* Practice subscription */}
          {practice && (
            <div className="disease-game-practice-account">
              <span>Signed in as {practiceAccount.email}</span>
              <span>
                <button onClick={handleManageSubscription} className="disease-game-link-button">
                  Manage or cancel subscription
                </button>
                {' · '}
                <button onClick={signOutPractice} className="disease-game-link-button">
                  Sign out
                </button>
              </span>
              {paywallError && <p className="disease-game-paywall-error">{paywallError}</p>}
            </div>
          )}
          </>
          )}
        </div>

        {/* Instructions */}
        <div className="disease-game-instructions">
          <h3 className="disease-game-instructions-title">
            <AlertCircle className="disease-game-alert-icon" />
            How to Play
          </h3>
          <ul className="disease-game-instructions-list">
            <li className="disease-game-instruction-item">• One puzzle per day - same for all players</li>
            <li className="disease-game-instruction-item">• Guess the infectious disease based on its characteristics</li>
            <li className="disease-game-instruction-item">• Green = exact match, Yellow = partial match, Red = no match</li>
            <li className="disease-game-instruction-item">• Use the clues from each guess to narrow down the answer</li>
            <li className="disease-game-instruction-item">• Try to guess in as few attempts as possible!</li>
            <li className="disease-game-instruction-item">• Incubation periods are shown in days (min and max) with arrows indicating if you need to go higher ↑ or lower ↓</li>
            <li className="disease-game-instruction-item">• Hints unlock after 3 guesses (small hint) and 6 guesses (big hint) to help you narrow down the disease</li>
            <li className="disease-game-instruction-item">• A new disease appears every day at midnight UTC</li>
            <li className="disease-game-instruction-item">• Practice mode ($1/month): play unlimited random diseases without affecting your streak</li>
            <li className="disease-game-instruction-item">• <a href="https://docs.google.com/forms/d/1ooBNOeJ7SPnjHtUx8_Qe49uFMLkA88OFwsbwfrQrwjM/viewform?edit_requested=true" target="_blank" rel="noopener noreferrer" style={{color: '#93C5FD', textDecoration: 'underline'}}>Report errors or suggest improvements here</a></li>
          </ul>
        </div>

        {/* Footer */}
        <div className="disease-game-footer">
          <p className="disease-game-footer-text">
            Also try <a href="https://labbeaglegames.com/" target="_blank" rel="noopener noreferrer" className="disease-game-footer-link">Pharmageddon</a>!
          </p>
          <p className="disease-game-footer-text">
            Also try <a href="https://www.neurdle.com/" target="_blank" rel="noopener noreferrer" className="disease-game-footer-link">Neurdle</a>!
          </p>
          <p className="disease-game-footer-text">
            <a href="/terms.html" className="disease-game-footer-link">Terms</a>
            {' · '}
            <a href="/privacy.html" className="disease-game-footer-link">Privacy</a>
          </p>
        </div>
      </div>
    </div>
  );
}